import { useLayoutEffect, useRef, useState } from "react";

import UserChatBubble from "../Forms/FirmUserBtn";

const ROW_HEIGHT = 73;

const formatAddressLines = (contact) => {
  if (!contact) return [];
  const line1 = [contact.address1, contact.address2].filter((s) => s?.trim()).join(" ");
  const line2 = [contact.city, contact.state || contact.stateAbr, contact.zip]
    .filter((s) => s?.trim())
    .join(" ");
  return [line1, line2].filter(Boolean);
};

const SearchTables = ({
  mockFirms,
  showHeader = false,
  showNumber = true,
  loading = false,
  onFirmClick,
  fill = false, // fill parent height with fake rows; scroll when real rows overflow
}) => {
  const wrapperRef = useRef(null);
  const tbodyRef = useRef(null);
  const [fillerHeights, setFillerHeights] = useState([]);

  useLayoutEffect(() => {
    if (!fill) return undefined;
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;

    const compute = () => {
      const theadHeight = wrapper.querySelector("thead")?.offsetHeight ?? 0;
      const realRowsHeight = [
        ...(tbodyRef.current?.querySelectorAll("tr:not(.fake-row-2)") ?? []),
      ].reduce((sum, tr) => sum + tr.offsetHeight, 0);

      const available = wrapper.clientHeight - theadHeight - realRowsHeight;
      if (available <= 0) {
        setFillerHeights((prev) => (prev.length === 0 ? prev : []));
        return;
      }

      const fullRows = Math.floor(available / ROW_HEIGHT);
      const remainder = available % ROW_HEIGHT;
      setFillerHeights([
        ...Array(fullRows).fill(ROW_HEIGHT),
        ...(remainder > 0 ? [remainder] : []),
      ]);
    };

    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [fill, mockFirms, loading]);

  return (
    <div
      ref={wrapperRef}
      className="table--no-card rounded-0 border-0 w-100"
      style={
        fill
          ? {
              height: "calc(100% - 300px - 25px - 60px)",
              overflowY: "auto",
              scrollbarWidth: "none",
            }
          : undefined
      }
    >
      <table className="table table-borderless table-earning">
        {showHeader && (
          <thead className="sticked-head">
            <tr id="tb-header">
              <th
                className="td-autosize"
                style={{ width: "25px", minWidth: "25px", maxWidth: "25px" }}
              />
              <th colSpan={2} className="td-autosize text-center">
                Firm
              </th>
              <th />
              <th className="td-autosize text-center">Contact</th>
              <th />
              <th className="td-autosize text-center">Cases Handled</th>
              <th />
              <th className="td-autosize text-center">States Represented</th>
              <th
                className="td-autosize"
                style={{ width: "15px", minWidth: "15px", maxWidth: "15px" }}
              />
            </tr>
          </thead>
        )}
        <tbody ref={tbodyRef}>
          {loading && (
            <tr>
              <td colSpan={10} className="text-center">
                Loading firms...
              </td>
            </tr>
          )}

          {!loading && mockFirms?.length === 0 && showHeader && (
            <tr>
              <td colSpan={10} className="text-center">
                No firms found
              </td>
            </tr>
          )}

          {!loading &&
            mockFirms?.map((firm, index) => {
              const addressLines = formatAddressLines(firm.display_contact);
              return (
                <tr
                  key={firm.firm_id}
                  onClick={() => onFirmClick?.(firm)}
                  style={{ cursor: onFirmClick ? "pointer" : "default", height: `${ROW_HEIGHT}px` }}
                >
                  <td
                    style={{ width: "25px", minWidth: "25px", maxWidth: "25px" }}
                    id="client-comm-date-sep"
                  >
                    {showNumber ? index + 1 : null}
                  </td>

                  {/* Firm logo */}
                  <td className="text-center">
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{ width: "max-content" }}
                    >
                      {firm.logo && (
                        <img
                          src={firm.logo}
                          alt={firm.firm_name}
                          style={{ height: "63px", width: "100%", objectFit: "contain" }}
                        />
                      )}
                    </div>
                  </td>

                  {/* Firm name + address */}
                  <td className="text-start">
                    <div className="d-flex flex-column">
                      <span className="height-21" style={{ fontWeight: "600" }}>
                        {firm.firm_name}
                      </span>
                      {addressLines.map((line) => (
                        <span key={line} className="height-21" style={{ fontWeight: "600" }}>
                          {line}
                        </span>
                      ))}
                    </div>
                  </td>

                  <td />

                  {/* Contact */}
                  <td className="text-center" style={{ width: "25%", maxWidth: 0 }}>
                    <div
                      className="d-flex align-items-center flex-wrap justify-content-center"
                      style={{ gap: "5px" }}
                    >
                      {firm.chat_staff?.map((staff) => (
                        <UserChatBubble
                          userId={staff.user?.id}
                          key={staff.id}
                          first_name={staff.user?.first_name}
                          last_name={staff.user?.last_name}
                          profilePic={staff.profile_pic_29p ?? ""}
                          is_active={staff.user?.is_active}
                          width={35}
                          isActive={false}
                          hideIcon={true}
                        />
                      ))}
                    </div>
                  </td>

                  <td />

                  {/* Case types */}
                  <td className="text-black text-center" style={{ width: "20%", maxWidth: 0 }}>
                    <div className="d-flex flex-wrap justify-content-center" style={{ gap: "5px" }}>
                      {firm.case_types?.map((caseType) => (
                        <div key={caseType.id} className="d-flex align-items-center">
                          {caseType?.casetype_icon && (
                            <span className="ic-avatar ic-15 m-r-5 text-label color-grey-2 text-uppercase fw-bold d-flex align-items-center">
                              <img
                                style={{ width: "15px", height: "15px" }}
                                src={caseType.casetype_icon}
                                alt={caseType.name}
                              />
                            </span>
                          )}
                          <p
                            className="font-weight-semibold client-header-case-name"
                            style={{ color: "var(--primary)" }}
                          >
                            {caseType.name}
                          </p>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td />
                  {/* States */}
                  <td>
                    <div
                      className="d-flex flex-wrap justify-content-center"
                      style={{ gap: "10px" }}
                    >
                      {firm.states?.map((state) => (
                        <div
                          key={state.id}
                          className="d-flex align-items-center"
                          style={{ gap: "2px" }}
                        >
                          <div
                            className="d-flex align-items-center justify-content-center"
                            style={{ top: "2px", position: "relative" }}
                          >
                            <svg
                              style={{
                                width: "19px",
                                height: "19px",
                                fill: "var(--primary)",
                                color: "var(--primary)",
                                stroke: "var(--primary)",
                              }}
                              className={`icon icon-state-${state.StateAbr}`}
                            >
                              <use xlinkHref={`#icon-state-${state.StateAbr}`} />
                            </svg>
                          </div>
                          <span
                            className="height-21"
                            style={{ fontWeight: "600", color: "var(--primary)" }}
                          >
                            {state.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td />
                </tr>
              );
            })}

          {fill &&
            fillerHeights.map((height, index) => (
              <tr
                key={`fake-row-${index}`}
                className="fake-row-2"
                style={{ height: `${height}px` }}
              >
                <td colSpan={10}>&nbsp;</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
};

export default SearchTables;
